import { Component, DestroyRef, OnInit, WritableSignal, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { AzavistaApiError, AzavistaApiService, ILanguageItem, IUser } from '@azavista/servicelib';
import { MyAccountService } from '../my-account/my-account.service';
import { LocalStorageKey, SharedService, SubjectsService, TRANSLATION_MAP } from '../shared';

@Component({
    templateUrl: 'login.component.html',
    styleUrls: ['login.component.css'],
    standalone: false
})
export class LoginComponent implements OnInit {
    email = '';
    password = '';
    signals = this.createSignals();
    corporateLoginUrl: string;
    googleLoginUrl = 'https://accounts.google.com/';
    microsoftLoginUrl = 'https://login.microsoftonline.com/';
    signUpUrl = 'https://www.azavista.com/request-demo';
    language: string;
    availableLanguages: ILanguageItem[];;
    translations = TRANSLATION_MAP;
    isRevealPassword = false;
    isLoaded = false;

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private sharedSvc: SharedService, private translateSvc: TranslateService,
        private userAccountSvc: MyAccountService,
        private apiSvc: AzavistaApiService, private router: Router, private activatedRoute: ActivatedRoute,
        private readonly subjectsSvc: SubjectsService,
    ) { }

    ngOnInit() {
        this.corporateLoginUrl = this.sharedSvc.getWebsiteUrl('/auth/login');
        this.availableLanguages = this.userAccountSvc.languages;
        this.language = this.getInitLanguage();
        this.onLanguageSelected(this.language);
        this.subjectsSvc.getCurrentUser().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: currentUser => this.processCurrentUser(currentUser)
        });
    }

    processCurrentUser(user: IUser): void {
        if (user) {
            // Already authenticated - redirect
            // app.component will log error ExpressionChangedAfterItHasBeenCheckedError in its template at '[class.active-page]="activeTopMenuPage === 'general-dashboard'"'
            // if we redirect in the same tick - redirect on the next one
            this.sharedSvc.executeOnNextLoop(() => this.redirectAfterLoggedIn());
        }
        this.isLoaded = true;
    }

    async onLogin(): Promise<void> {
        if (!this.email || !this.password) {
            return;
        }

        try {
            this.signals.loginError.set(null);
            await this.apiSvc.authenticate({
                username: this.email, password: this.password
            });
            await this.apiSvc.setCurrentUserFromTokenInfo();
            this.redirectAfterLoggedIn();
        } catch (err) {
            const azavistaErr = err as AzavistaApiError;
            const isAccountExpired = azavistaErr.communicationError?.body?.code === 'AccountExpired';
            this.signals.loginError.set({
                accountExpired: isAccountExpired,
                loginErrorTextTranslationKey: isAccountExpired ? TRANSLATION_MAP.CREDENTIALS_EXPIRED : TRANSLATION_MAP.INVALID_USERNAME_OR_PASSWORD,
            });
        }
    }

    redirectAfterLoggedIn(): void {
        const returnUrl = this.activatedRoute.snapshot.queryParamMap.get('returnUrl');
        if (returnUrl) {
            window.location.href = returnUrl;
        } else {
            // TODO: Leave main component to decide about where to navigate
            this.router.navigate(['/dashboard']);
        }
    }

    onLanguageSelected(lang: string): void {
        this.translateSvc.setDefaultLang(lang);
        this.translateSvc.use(lang);
        window.localStorage.setItem(LocalStorageKey.userLanguage, lang);
    }

    getInitLanguage(): string {
        const localStorageLanguage = window.localStorage.getItem(LocalStorageKey.userLanguage);
        if (localStorageLanguage) {
            return localStorageLanguage;
        }
        const browserLanguage = this.translateSvc.getBrowserLang();
        const supportedBrowserLanguage = this.availableLanguages.find(x => x.id.includes(browserLanguage));
        if (supportedBrowserLanguage?.id) {
            return supportedBrowserLanguage.id;
        }
        const platformDefaultLanguage = this.translateSvc.getDefaultLang();
        return platformDefaultLanguage;
    }

    toggleRevealPassword() {
        this.isRevealPassword = !this.isRevealPassword;
    }

    createSignals(): Signals {
        const signals: Signals = {
            loginError: signal(null),
        };
        return signals;
    }
}

interface Signals {
    loginError: WritableSignal<LoginError | null>;
}

interface LoginError {
    accountExpired?: boolean;
    loginErrorTextTranslationKey?: string;
}
